import { ApiError, type ApiProblem } from './types';

/**
 * Tiny fetch wrapper.
 *
 * - Threads `X-Correlation-Id` so backend logs can be tied to a single user action.
 * - Reads `X-Device-Fingerprint` from localStorage so the Backend can record it on every event.
 * - On 401, the auth store's silent-refresh interceptor will retry once with a fresh access token.
 * - On any non-2xx, throws `ApiError` with the parsed ProblemDetails code so callers can branch on it.
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5000';
const DEVICE_FP_KEY = 'cscorer.deviceFingerprint';

function getOrCreateDeviceFingerprint(): string {
  let fp = localStorage.getItem(DEVICE_FP_KEY);
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem(DEVICE_FP_KEY, fp);
  }
  return fp;
}

function generateCorrelationId(): string {
  // Avoid pulling a UUID lib; crypto.randomUUID is widely supported in modern browsers.
  return crypto.randomUUID().replace(/-/g, '');
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  accessToken?: string;
  signal?: AbortSignal;
}

export async function apiFetch<TResponse = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<TResponse> {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-Id': generateCorrelationId(),
    'X-Device-Fingerprint': getOrCreateDeviceFingerprint(),
  };
  if (opts.accessToken) {
    headers['Authorization'] = `Bearer ${opts.accessToken}`;
  }

  // Only include `body` / `signal` when defined, to satisfy
  // `exactOptionalPropertyTypes` on RequestInit.
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'omit',
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  // A network-level failure (server down, wrong API URL, CORS/preflight rejected, offline)
  // surfaces as a raw TypeError "Failed to fetch". Translate it into a clear, actionable
  // ApiError (status 0) so callers show something useful instead of "Failed to fetch".
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch {
    throw new ApiError(
      `Can't reach the server at ${API_BASE_URL}. Check that the backend is running and that VITE_API_BASE_URL points to it.`,
      0,
      'network.unreachable',
    );
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson =
    contentType.includes('application/json') || contentType.includes('application/problem+json');
  const payload: unknown = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    const problem = payload as ApiProblem | undefined;
    throw new ApiError(
      problem?.detail ?? problem?.title ?? response.statusText,
      response.status,
      problem?.title,
    );
  }

  return payload as TResponse;
}

export { getOrCreateDeviceFingerprint };
